/**
 * StandardText Types for MEV-Med Frontend
 * Type definitions for standard text management
 *
 * @version 2025.1.0
 * @description Tipos TypeScript para textos padronizados
 */

/**
 * Enum for StandardText categories
 */
export enum StandardTextCategory {
  EXAM_INSTRUCTION = 'EXAM_INSTRUCTION',
  MEDICAL_REPORT = 'MEDICAL_REPORT',
  EMAIL_TEMPLATE = 'EMAIL_TEMPLATE',
  SMS_TEMPLATE = 'SMS_TEMPLATE',
  WHATSAPP_TEMPLATE = 'WHATSAPP_TEMPLATE',
  DOCUMENT_HEADER = 'DOCUMENT_HEADER',
  DOCUMENT_FOOTER = 'DOCUMENT_FOOTER',
  LEGAL_DISCLAIMER = 'LEGAL_DISCLAIMER',
  PRIVACY_NOTICE = 'PRIVACY_NOTICE',
  PCMSO_REPORT = 'PCMSO_REPORT',
  PPRA_REPORT = 'PPRA_REPORT',
  LTCAT_REPORT = 'LTCAT_REPORT',
  PGR_REPORT = 'PGR_REPORT',
  LAUDO_REPORT = 'LAUDO_REPORT',
  CERTIFICATE = 'CERTIFICATE',
  ASO_REPORT = 'ASO_REPORT',
  APPOINTMENT_INSTRUCTION = 'APPOINTMENT_INSTRUCTION',
  CONSENT_FORM = 'CONSENT_FORM',
  MEDICAL_PRESCRIPTION = 'MEDICAL_PRESCRIPTION',
  REFERRAL = 'REFERRAL',
  ORIENTATION = 'ORIENTATION',
  PROCEDURE = 'PROCEDURE',
  CONCLUSION = 'CONCLUSION',
  OBSERVATION = 'OBSERVATION',
  RECOMMENDATION = 'RECOMMENDATION',
  PCMSO_ANALYTICAL_REPORT = 'PCMSO_ANALYTICAL_REPORT',
  LI_REPORT = 'LI_REPORT',
  LP_REPORT = 'LP_REPORT',
  AET_REPORT = 'AET_REPORT',
  APR_REPORT = 'APR_REPORT',
  ACTION_PLAN = 'ACTION_PLAN',
  QUADRO_INSALUBRIDADE = 'QUADRO_INSALUBRIDADE',
  OTHER = 'OTHER'
}

/**
 * Enum for StandardText status
 */
export enum StandardTextStatus {
  ATIVO = 'ATIVO',
  INATIVO = 'INATIVO'
}

/**
 * StandardText entity interface
 */
export interface StandardText {
  id: number;
  title: string;
  content: string;
  category: StandardTextCategory;
  /** Seção do documento a que este texto pertence; nulo vale para todas. */
  sectionKey: string | null;
  companyId: number | null;
  companyName?: string;
  isGlobal: boolean;
  variables: string[];
  status: StandardTextStatus;
  createdAt: string;
  updatedAt: string;
}

/**
 * Filters for listing standard texts
 */
export interface StandardTextFilters {
  search?: string;
  category?: StandardTextCategory;
  companyId?: number;
  isGlobal?: boolean;
  status?: StandardTextStatus;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

/**
 * Form data for creating/updating standard texts
 */
export interface StandardTextFormData {
  title: string;
  content: string;
  category: StandardTextCategory;
  sectionKey?: string | null;
  companyId?: number | null;
  isGlobal: boolean;
  variables?: string[]; // Reserved for future use - not currently exposed in UI
  status: StandardTextStatus;
}

/**
 * Seção de um documento, como o registry do backend a descreve
 * (`GET /standard-texts/document-sections`)
 */
export interface DocumentSection {
  key: string;
  title: string;
  order: number;
  required: boolean;
  category: StandardTextCategory;
}

/**
 * Documento do catálogo com as seções que ele emite
 */
export interface DocumentSectionCatalog {
  type: string;
  label: string;
  sections: DocumentSection[];
}

/**
 * Variável oferecida a quem escreve o texto (`GET /standard-texts/variables`)
 */
export interface DocumentVariable {
  token: string;
  label: string;
  example: string;
}

/**
 * Corpo da prévia (`POST /standard-texts/preview`)
 */
export interface StandardTextPreviewRequest {
  content: string;
  companyId: number;
  departmentId?: number;
  sectionKey?: string;
}

/**
 * Prévia resolvida: HTML já sanitizado pelo backend e os tokens que ficaram
 * sem valor
 */
export interface StandardTextPreviewResult {
  html: string;
  variaveisNaoResolvidas: string[];
}

/**
 * Response for list endpoint with pagination
 */
export interface StandardTextListResponse {
  texts: StandardText[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * Response for single standard text endpoints
 */
export interface StandardTextResponse {
  data: StandardText;
  message?: string;
}

/**
 * Response for stats endpoint
 */
export interface StandardTextStats {
  total: number;
  byStatus: Record<StandardTextStatus, number>;
  byCategory: Record<string, number>;
  globalCount: number;
  companySpecificCount: number;
  totalVariables: number;
}

/**
 * Category option for select inputs
 */
export interface CategoryOption {
  value: StandardTextCategory;
  label: string;
}

/**
 * Clone text data
 */
export interface CloneTextData {
  targetCompanyId: number;
  title?: string;
  content?: string;
}

// ============================================================================
// RESERVED FOR FUTURE USE - Variable processing functionality
// These types are not currently used in the UI but are kept for future integration
// when StandardText is linked to entities like Consultations, Exams, etc.
// ============================================================================

/**
 * Preview request data
 * @reserved Future use - when variables will be populated from entity data
 */
export interface PreviewData {
  textId: number;
  sampleData?: Record<string, string>;
}

/**
 * Variable validation result
 * @reserved Future use - when variable processing is implemented
 */
export interface VariableValidation {
  valid: boolean;
  extractedVariables: string[];
  declaredVariables: string[];
  missing: string[];
  unused: string[];
}
